import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
  TextField
} from '@material-ui/core';
import PaymentSuccessDialog from './PaymentSuccessDialog';

const FormContainer = styled.div`
  background: linear-gradient(145deg, #2a2a2a, #1f1f1f);
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 480px;
  margin: 0 auto;
`;

const Field = styled.div`
  margin-bottom: 18px;

  & .MuiOutlinedInput-root {
    background: #3a3a3a;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: #404040;
      box-shadow: 0 0 12px rgba(100, 100, 255, 0.15);
    }

    &.Mui-focused {
      background: #404040;
      box-shadow: 0 0 16px rgba(100, 100, 255, 0.3);
    }

    & fieldset {
      border-color: #4a4a4a;
    }

    &:hover fieldset {
      border-color: #6464ff;
    }

    &.Mui-focused fieldset {
      border-color: #6464ff;
    }
  }

  & .MuiInputLabel-root {
    color: #b0b0b0;
  }

  & .MuiInputLabel-root.Mui-focused {
    color: #6464ff;
  }

  & .MuiOutlinedInput-input {
    color: #e5e5e5;
  }
`;

const CardShell = styled.div`
  padding: 16px;
  border: 1px solid #4a4a4a;
  border-radius: 8px;
  background: #3a3a3a;
  transition: all 0.3s ease;

  &:hover {
    background: #404040;
    border-color: #6464ff;
    box-shadow: 0 0 12px rgba(100, 100, 255, 0.15);
  }

  & .StripeElement {
    color: #e5e5e5;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  background: ${(props) => (props.disabled ? '#2a2a2a' : 'linear-gradient(135deg, #8b5cf6, #6366f1)')};
  color: #ffffff;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${(props) => (props.disabled ? 'none' : '0 0 20px rgba(139, 92, 246, 0.6)')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #a78bfa, #818cf8);
    box-shadow: 0 0 30px rgba(139, 92, 246, 0.8), 0 0 60px rgba(139, 92, 246, 0.4);
    transform: translateY(-2px);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 0 25px rgba(139, 92, 246, 0.9);
  }

  &:disabled {
    background: #2a2a2a;
    color: #666666;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  color: #d0d0d0;
`;

const Note = styled.div`
  font-size: 12px;
  color: #909090;
  margin-top: 12px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &::before {
    content: '🔒';
    font-size: 14px;
  }
`;

const CheckboxContainer = styled.div`
  margin: 16px 0;

  & .MuiCheckbox-root {
    color: #6464ff;

    &:hover {
      background-color: rgba(100, 100, 255, 0.08);
    }
  }

  & .MuiCheckbox-colorPrimary.Mui-checked {
    color: #6464ff;
  }

  & .MuiTypography-root {
    color: #d0d0d0;
  }

  & a {
    color: #8484ff;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      color: #a4a4ff;
      text-shadow: 0 0 8px rgba(100, 100, 255, 0.5);
    }
  }
`;

const TaxBreakdown = styled.div`
  background: linear-gradient(145deg, #2d2d2d, #252525);
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid #4a4a4a;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const apiBaseUrl = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
  : process.env.NODE_ENV === 'development'
  ? 'http://localhost:3008'
  : 'https://yielding-linnell-ev-payment-a515489f.koyeb.app';

const StripePaymentForm = ({ price, description = 'Computer service / programming session', autoCheckTerms }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [billingDetails, setBillingDetails] = useState({ name: '', email: '' });
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  React.useEffect(() => {
    if (autoCheckTerms && !acceptedTerms) {
      setAcceptedTerms(true);
    }
  }, [autoCheckTerms, acceptedTerms]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not initialized. Please refresh.');
      return;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms & Conditions');
      return;
    }
    if (billingDetails.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingDetails.email)) {
      setError('Invalid email format');
      return;
    }
    if (price * 100 < 50) {
      setError('Amount must be at least €0.50');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const createRes = await fetch(`${apiBaseUrl}/api/create-stripe-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(price * 100),
          currency: 'eur',
          billingDetails,
          metadata: {
            service_description: description,
            terms_accepted: true
          }
        })
      });

      const data = await createRes.json();
      if (!createRes.ok) throw new Error(data.error || 'Failed to create payment');

      const { clientSecret, paymentIntentId } = data;

      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: billingDetails.name, email: billingDetails.email }
        },
        receipt_email: billingDetails.email
      });

      if (stripeErr) throw new Error(stripeErr.message);

      if (paymentIntent.status === 'succeeded') {
        setShowSuccessDialog(true);
        setConfirmationLoading(true);

        // Optional server confirmation (protected with CSRF on server)
        fetch(`${apiBaseUrl}/api/confirm-stripe-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId,
            paymentMethodId: paymentIntent.payment_method,
            receiptEmail: billingDetails.email
          })
        }).finally(() => setConfirmationLoading(false));

        // Reset form
        setBillingDetails({ name: '', email: '' });
        elements.getElement(CardElement).clear();
        setAcceptedTerms(false);
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Try again.');
    } finally {
      setProcessing(false);
    }
  };

  const TermsContent = () => (
    <>
      <DialogTitle style={{ color: '#e5e5e5', borderBottom: '1px solid #4a4a4a' }}>Terms &amp; Conditions</DialogTitle>
      <DialogContent dividers style={{ borderColor: '#4a4a4a' }}>
        <Typography variant='h6' gutterBottom>
          Scope of Service
        </Typography>
        <Typography paragraph>
          You are purchasing professional <strong>computer services and/or online programming lessons</strong>. No physical goods are sold. Work is
          delivered remotely via video calls, screen-share, or asynchronous support.
        </Typography>

        <Typography variant='h6' gutterBottom>
          Use &amp; Compliance
        </Typography>
        <Typography paragraph>
          You agree not to use our services for any <strong>illegal</strong> purpose or to support restricted activities. For details, see{' '}
          <Link href='https://stripe.com/restricted-businesses' target='_blank' rel='noopener noreferrer'>
            Stripe’s Restricted Businesses
          </Link>
          .
        </Typography>

        <Typography variant='h6' gutterBottom>
          Scheduling, Cancellations &amp; Refunds
        </Typography>
        <Typography component='div'>
          <ul>
            <li>Sessions are booked by the hour unless otherwise agreed in writing.</li>
            <li>
              Free cancellations or rescheduling up to <strong>24 hours</strong> before the session.
            </li>
            <li>
              Within 24 hours, cancellations may be charged up to <strong>100% of the booked hour(s)</strong>.
            </li>
            <li>
              Completed time is <strong>non-refundable</strong>; unused prepaid time can be rescheduled within 60 days.
            </li>
          </ul>
        </Typography>

        <Typography variant='h6' gutterBottom>
          Liability
        </Typography>
        <Typography paragraph>
          Best-effort guidance is provided, but results depend on your environment and implementation. Our liability is limited to the amount paid for
          the specific session.
        </Typography>

        <Typography variant='h6' gutterBottom>
          Payment Processor
        </Typography>
        <Typography paragraph>
          Payments are processed by Stripe. By paying, you accept the applicable Stripe terms and acknowledge processing under{' '}
          <Link href='https://stripe.com/privacy' target='_blank' rel='noopener noreferrer'>
            Stripe’s Privacy Policy
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTermsOpen(false)} color='primary'>
          Close
        </Button>
      </DialogActions>
    </>
  );

  const PrivacyContent = () => (
    <>
      <DialogTitle style={{ color: '#e5e5e5', borderBottom: '1px solid #4a4a4a' }}>Privacy Policy</DialogTitle>
      <DialogContent dividers style={{ borderColor: '#4a4a4a' }}>
        <Typography paragraph>
          We collect your name and email to schedule, deliver service, and send receipts. Payment details are handled by Stripe — we do not store card
          numbers.
        </Typography>
        <Typography component='div'>
          <ul>
            <li>Data is retained only as long as needed for accounting and legal requirements.</li>
            <li>You may request access, correction, or deletion by contacting us at the email below.</li>
          </ul>
        </Typography>
        <Typography paragraph>Contact: support@your-domain.example</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPrivacyOpen(false)} color='primary'>
          Close
        </Button>
      </DialogActions>
    </>
  );

  return (
    <>
      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
            color: '#e5e5e5',
            border: '1px solid #4a4a4a'
          }
        }}>
        <TermsContent />
      </Dialog>
      <Dialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
            color: '#e5e5e5',
            border: '1px solid #4a4a4a'
          }
        }}>
        <PrivacyContent />
      </Dialog>

      {error && (
        <div
          style={{
            color: '#fca5a5',
            background: '#3a2a2a',
            border: '1px solid #7f1d1d',
            padding: '12px',
            borderRadius: 8,
            marginBottom: 16,
            boxShadow: '0 0 12px rgba(255, 100, 100, 0.2)'
          }}>
          {error}
        </div>
      )}

      <PaymentSuccessDialog open={showSuccessDialog} loading={confirmationLoading} onClose={() => setShowSuccessDialog(false)} />

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <Field>
            <TextField
              fullWidth
              label='Full name'
              variant='outlined'
              size='small'
              value={billingDetails.name}
              onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
              required
            />
          </Field>

          <Field>
            <TextField
              fullWidth
              label='Email'
              type='email'
              variant='outlined'
              size='small'
              value={billingDetails.email}
              onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
              required
            />
          </Field>

          <CardShell>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#e5e5e5',
                    '::placeholder': {
                      color: '#909090'
                    }
                  },
                  invalid: {
                    color: '#fca5a5'
                  }
                },
                hidePostalCode: true
              }}
            />
          </CardShell>

          <CheckboxContainer>
            <FormControlLabel
              control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} color='primary' />}
              label={
                <Typography variant='body2'>
                  I agree to the{' '}
                  <Link
                    href='#'
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsOpen(true);
                    }}>
                    Terms &amp; Conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    href='#'
                    onClick={(e) => {
                      e.preventDefault();
                      setPrivacyOpen(true);
                    }}>
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              }
            />
          </CheckboxContainer>

          {acceptedTerms && (
            <TaxBreakdown>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d0d0d0', marginBottom: 8 }}>
                <span>Subtotal:</span>
                <span>€{(price / 1.05).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d0d0d0', marginBottom: 8 }}>
                <span>Tax fee:</span>
                <span>€{(price - price / 1.05).toFixed(2)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#a78bfa',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  borderTop: '1px solid #4a4a4a',
                  paddingTop: 8,
                  textShadow: '0 0 10px rgba(167, 139, 250, 0.5)'
                }}>
                <span>Total:</span>
                <span>€{price.toFixed(2)}</span>
              </div>
            </TaxBreakdown>
          )}

          <SubmitBtn type='submit' disabled={!stripe || processing || !acceptedTerms}>
            {processing ? 'Processing…' : `Pay €${price.toFixed(2)}`}
          </SubmitBtn>
          <Note>Payments are securely processed by Stripe.</Note>
        </form>
      </FormContainer>
    </>
  );
};

export default StripePaymentForm;
